import { Label as ShadcnLabel } from "@/components/ui/label";
import { Tooltip, TooltipTrigger } from "@/domains/shared/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { CircleHelp as HelpCircle } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";

interface LabelProps extends ComponentProps<typeof ShadcnLabel> {
    children: ReactNode;
    isInvalid?: boolean;
    isRequired?: boolean;
    tooltip?: string;
    tooltipDescription?: string;
    ref?: Ref<HTMLLabelElement>;
}

export const Label = ({ isInvalid, isRequired, tooltip, tooltipDescription, className, ...props }: LabelProps) => {
    return (
        <ShadcnLabel
            // Used for conditionally hiding/showing the label element via CSS:
            // <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
            // or
            // <Input label="Visible only on mobile" className="lg:label:hidden" />
            data-label="true"
            {...props}
            className={cx("flex cursor-default items-center gap-0.5 text-sm font-medium text-foreground", className)}
        >
            {props.children}

            <span
                aria-hidden="true"
                className={cx(
                    "hidden",
                    isRequired && "block text-destructive",
                    typeof isRequired === "undefined" && "group-required:block group-required:text-destructive",

                    isInvalid && "text-destructive",
                    typeof isInvalid === "undefined" && "group-invalid:text-destructive",
                )}
            >
                *
            </span>

            {tooltip && (
                <Tooltip title={tooltip} description={tooltipDescription} placement="top">
                    <TooltipTrigger
                        // `TooltipTrigger` inherits the disabled state from the parent form field
                        // but we don't that. We want the tooltip be enabled even if the parent
                        // field is disabled.
                        isDisabled={false}
                        className="cursor-pointer text-muted-foreground transition-colors hover:text-foreground focus:text-foreground"
                    >
                        <HelpCircle className="size-4" />
                    </TooltipTrigger>
                </Tooltip>
            )}
        </ShadcnLabel>
    );
};

Label.displayName = "Label";
